import React from 'react';
import { Spinner, Box, Tooltip, IconButton } from '@chakra-ui/core';
import { useDispatch } from 'react-redux';
import { Table, Thead, THeadRow, TH, TBodyRow, TD } from '../common/components/Table';
import {
  EmptyTasksMessage,
  TableEditLink,
  TASKS_TYPES,
  TypeTag,
  TBody,
  THFilter,
} from './components';
import { getTaskRedirection } from '../common/routes';
import Can from '../roleBasedAccess/Can';
import { tasksAPI } from '../common/api/tasks';
import { toastActions } from '../redux/toast/actions';
import { TOAST_MESSAGE } from '../common/components/Toasts/Toast';

export const AllTasksTab = ({
  tasks,
  tags: allTags,
  operators: allOperators,
  details: allDetails,
  filters,
  onFilterChange,
  onDeleteTask,
  pending,
  pagination,
}) => {
  const dispatch = useDispatch();

  if (pending) {
    return (
      <Box color="semiDarkGray.600" mt={8} textAlign="center">
        <Spinner size={8} label="Chargement..." />
      </Box>
    );
  }

  const handleDeleteValidation = async taskId => {
    try {
      await tasksAPI.deleteTask(taskId);
      dispatch(toastActions.success(TOAST_MESSAGE.success.taskDelete));
      onDeleteTask();
    } catch (err) {
      dispatch(toastActions.error(TOAST_MESSAGE.error.taskDelete));
    }
  };

  return (
    <>
      <Table d="flex" flexDirection="column" px={8}>
        <Thead d="block" pos="sticky" zIndex="docked">
          <Can
            perform="tasks:operator:read"
            yes={({ children }) => (
              <THeadRow
                d="grid"
                gridTemplateColumns="100px 0.5fr 0.45fr 0.55fr 0.75fr 1fr 100px 100px"
              >
                {children}
              </THeadRow>
            )}
            no={({ children }) => (
              <THeadRow d="grid" gridTemplateColumns="100px 0.5fr 0.5fr 0.75fr 1fr 100px 100px">
                {children}
              </THeadRow>
            )}
          >
            <TH>Dossier</TH>
            <TH>Bénéficiaire</TH>
            <TH>
              <THFilter
                options={allTags}
                label="Tag"
                menuOptionGroupOptions={{
                  onChange: value => onFilterChange('tags', value),
                  value: filters.tags,
                }}
                isUpppercase
              />
            </TH>
            <Can
              perform="tasks:operator:read"
              yes={() => (
                <TH>
                  <THFilter
                    options={allOperators}
                    label="Opérateurs"
                    menuOptionGroupOptions={{
                      onChange: value => onFilterChange('operators', value),
                      value: filters.operators,
                    }}
                  />
                </TH>
              )}
            />
            <TH>Type de tâche</TH>
            <TH>
              <THFilter
                options={allDetails}
                label="Détail"
                menuOptionGroupOptions={{
                  onChange: value => onFilterChange('details', value),
                  value: filters.details,
                }}
                placement="bottom-center"
              />
            </TH>
            <TH>Délai</TH>
            <TH size="sm">Actions</TH>
          </Can>
        </Thead>

        <TBody>
          {tasks.length === 0 ? (
            <EmptyTasksMessage colSpan={8} />
          ) : (
            tasks.map(
              ({
                id,
                folderId,
                folderFormattedId,
                recipientFirstName,
                recipientLastName,
                userFirstName,
                userLastName,
                tags,
                taskType,
                details,
                delay,
                ergoUserKind,
                ...task
              }) => {
                const taskLink = getTaskRedirection({ ...task, folderId });

                return (
                  <Can
                    key={id}
                    perform="tasks:operator:read"
                    yes={({ children }) => (
                      <TBodyRow
                        hasScaleOnHover={false}
                        d="grid"
                        gridTemplateColumns="100px 0.5fr 0.45fr 0.55fr 0.75fr 1fr 100px 100px"
                      >
                        {children}
                      </TBodyRow>
                    )}
                    no={({ children }) => (
                      <TBodyRow
                        key={id}
                        hasScaleOnHover={false}
                        d="grid"
                        gridTemplateColumns="100px 0.5fr 0.5fr 0.75fr 1fr 100px"
                      >
                        {children}
                      </TBodyRow>
                    )}
                  >
                    <TD>
                      <TableEditLink to={taskLink} py={2} whiteSpace="nowrap">
                        {folderFormattedId}
                        {ergoUserKind === 'externe' ? ' *' : null}
                      </TableEditLink>
                    </TD>
                    <TD>
                      <TableEditLink to={taskLink} py={2}>
                        {recipientFirstName} {recipientLastName.toUpperCase()}
                      </TableEditLink>
                    </TD>
                    <TD>
                      <TableEditLink to={taskLink} py={2} textTransform="uppercase">
                        {tags.join(', ')}
                      </TableEditLink>
                    </TD>
                    <Can
                      perform="tasks:operator:read"
                      yes={() => (
                        <TD>
                          <TableEditLink to={taskLink} py={2}>
                            {userFirstName} {userLastName.toUpperCase()}
                          </TableEditLink>
                        </TD>
                      )}
                    />
                    <TD>
                      <TableEditLink to={taskLink} py={2}>
                        <TypeTag
                          variantColor={Object.keys(TASKS_TYPES).find(
                            key => TASKS_TYPES[key] === taskType,
                          )}
                          fontWeight="normal"
                        >
                          {taskType}
                        </TypeTag>
                      </TableEditLink>
                    </TD>
                    <TD>
                      <TableEditLink to={taskLink} py={2}>
                        {details}
                      </TableEditLink>
                    </TD>
                    <TD>
                      <TableEditLink to={taskLink} py={2} whiteSpace="nowrap" fontWeight="bold">
                        J + {delay}
                      </TableEditLink>
                    </TD>
                    <TD>
                      <Can
                        perform="tasks:delete_task"
                        yes={() => (
                          <Tooltip
                            zIndex={1000}
                            bg="semiDarkGray.900"
                            aria-label="Supprimer la tâche"
                            label="Supprimer la tâche"
                            placement="top"
                          >
                            <IconButton
                              icon="trash"
                              variant="ghost"
                              variantColor={null}
                              onClick={() => {
                                if (
                                  window.confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')
                                ) {
                                  handleDeleteValidation(id);
                                }
                              }}
                              color="coral.900"
                              height={8}
                              minW={8}
                              rounded="full"
                              aria-label="Supprimer"
                              _hover={{
                                '&:not([aria-disabled])': {
                                  color: 'white',
                                  backgroundColor: 'coral.900',
                                },
                              }}
                              _focus={{ shadow: 'none' }}
                            />
                          </Tooltip>
                        )}
                      />
                    </TD>
                  </Can>
                );
              },
            )
          )}
        </TBody>
      </Table>
      {pagination}
    </>
  );
};
